/**
 * @license
 * @copyright Copyright Motili Inc., 2021 All Rights Reserved
 */

import { ReactElement } from 'react';
import { Heading1, Paragraph } from '@motili/ui';
import CenteredContainer from '../../../components/layout/CenteredContainer';
import UnorderedList from '../../../components/UnorderedList';
import { useAppDispatch, useAppSelector } from '../../../common/redux/hooks';
import { styled } from 'styled-components';
import { resetState } from '../../../common/redux/ducks/contractorRegistrationForm';
import StyledButton from '../../../components/styled/StyledButton';
import BottomButtonNavigation from '../../../navigation/BottomButtonNavigation';
import type { StarterInfo } from '../../../common/types';

interface WelcomeProps {
    handleNextClick: () => void;
}

export default function Welcome({
    handleNextClick,
}: WelcomeProps): ReactElement {
    const dispatch = useAppDispatch();
    const starterInfo = useAppSelector(
        ({ contractorRegistration }) => contractorRegistration.starterInfo
    );

    const startOver = () => {
        dispatch(resetState());
    };

    return (
        <CenteredContainer>
            <Heading starterInfo={starterInfo} startOver={startOver} />
            <Paragraph>
                To get started we will need to ask you some questions and we
                require paperwork in order to verify you are eligible to work
                with us. Along with some general company information, here are
                the specific forms we will need from you to get started:
            </Paragraph>
            <UnorderedList>
                <li>EPA certification</li>
                <li>Secretary of State Registration Certification</li>
                <li>Tax ID number</li>
                <li>W-9 information</li>
            </UnorderedList>
            <Paragraph>
                If you will be registering your company for labor jobs, there
                are some additional forms required to complete your
                registration. These include:
            </Paragraph>
            <UnorderedList>
                <li>Trade License</li>
                <li>Certificate of Insurance</li>
            </UnorderedList>
            <br />
            <BottomButtonNavigation
                nextDisabled={false}
                onNextClick={handleNextClick}
            />
        </CenteredContainer>
    );
}

interface HeadingProps {
    starterInfo: StarterInfo;
    startOver: () => void;
}

const Heading = ({ starterInfo, startOver }: HeadingProps) => {
    if (starterInfo?.firstName) {
        const welcomeMessage = `Welcome back, ${starterInfo.firstName}!`;
        return (
            <>
                <Heading1>{welcomeMessage}</Heading1>
                <StyledParagraph>
                    To continue where you left off, click the Next button below
                    or click Start Over to start fresh.
                    <StyledButton onClick={startOver}>Start Over</StyledButton>
                </StyledParagraph>
            </>
        );
    }
    return <Heading1>Welcome</Heading1>;
};

const StyledParagraph = styled(Paragraph)`
    ${props => `background-color: ${props.theme.color('gray500')}`};
    border-radius: 5px;
    padding: 15px;
`;
